import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import routeNames from '../../../router/routeNames'
import ChannelsService from '../../../services/channelsService'

const MAX_SCAN_CHECK_RETRIES = 15
const SCAN_CHECK_DELAY_MS = 3000

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const useQrChannelConnection = (config) => {
   const {
      selectQrLoading,
      selectQrValue,
      selectSourceId,
      fetchQrThunk,
      startScanThunk,
      clearAction
   } = config

   const qrLoading = useSelector(selectQrLoading)
   const qrValue = useSelector(selectQrValue)
   const sourceId = useSelector(selectSourceId)

   const [isScanningStarted, setIsScanningStarted] = useState(false)
   const [isChannelScanned, setIsChannelScanned] = useState(false)
   const shouldStopCheckingIsQrScanned = useRef(false)

   const dispatch = useDispatch()
   const navigate = useNavigate()

   const handleFetchQrCode = useCallback(() => {
      dispatch(fetchQrThunk())
   }, [dispatch, fetchQrThunk])

   const handleStartChannelScan = useCallback(async (value) => {
      const data = await dispatch(startScanThunk({ sourceId: value }))
      if (data) setIsScanningStarted(true)
   }, [dispatch, startScanThunk])

   useEffect(() => {
      handleFetchQrCode()

      return () => {
         dispatch(clearAction())
         shouldStopCheckingIsQrScanned.current = true
      }
   }, [dispatch, handleFetchQrCode, clearAction])

   useEffect(() => {
      if (!sourceId) return

      handleStartChannelScan(sourceId)
   }, [sourceId, handleStartChannelScan])

   useEffect(() => {
      if (!isScanningStarted || !sourceId) return

      const checkIsQrCodeScanned = async (tryNumber = 1) => {
         try {
            const data = await ChannelsService.checkIsQrCodeScanned(sourceId)
            if (data.message === 'Канал установлен') {
               return setIsChannelScanned(true)
            }
         } catch (err) {
            const canRetry = err?.response?.data?.message === 'QR код не считан'
               && tryNumber < MAX_SCAN_CHECK_RETRIES
               && !shouldStopCheckingIsQrScanned.current

            if (canRetry) {
               await sleep(SCAN_CHECK_DELAY_MS)
               await checkIsQrCodeScanned(tryNumber + 1)
            }
         }
      }

      checkIsQrCodeScanned()
   }, [isScanningStarted, sourceId])

   useEffect(() => {
      if (isChannelScanned) navigate(routeNames.MESSENGERS)
   }, [isChannelScanned, navigate])

   return { qrLoading, qrValue }
}

export default useQrChannelConnection
